import { CanActivateFn, Router } from '@angular/router';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UserStateService } from 'riterz-core';
import { filter, map, of, take, timeout } from 'rxjs';
import { isSuperAdmin, userOrganizations } from '../utils/user';

/**
 * Requires the authenticated user to belong to at least one organization.
 *
 * **Layer 3** — always placed after `loggedInGuard` and `approvedGuard`. Angular
 * evaluates `canActivate` sequentially and short-circuits on the first
 * non-`true` result, so a pending or rejected user is caught by `approvedGuard`
 * and never reaches this gate.
 *
 * Redirects to `/onboarding`, and only there. Exemptions:
 * - super-admins, who routinely have no organization but need `/admin`
 * - the OAuth token hand-off, which `AppComponent` owns
 * - the invitation acceptance flow, which grants membership moments later
 *
 * SSR: returns `true` (user data unavailable server-side, prevents 504 timeouts).
 *
 * Do NOT apply to `/onboarding`, `/select-organization`, `/account`, `/admin` or
 * the status pages — those are how an organization-less user gets unstuck.
 */
export const organizationGuard: CanActivateFn = (
  route,
  state,
  platformId = inject(PLATFORM_ID),
  router: Router = inject(Router),
  userStateService: UserStateService = inject(UserStateService),
) => {
  // Skip on server — user data isn't available during SSR
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  // Let OAuth redirects through — AppComponent will process tokens and redirect
  if (route.queryParams['refresh_token'] && route.queryParams['access_token']) {
    return true;
  }

  // Don't interrupt invitation acceptance — membership is granted mid-flight
  if (route.queryParams['invitation_token']) {
    return true;
  }

  return userStateService.currentUser.pipe(
    filter(user => user !== null),
    take(1),
    map(user => {
      if (isSuperAdmin(user)) {
        return true;
      }

      if (userOrganizations(user).length === 0) {
        return router.createUrlTree(['/onboarding']);
      }

      return true;
    }),
    // Fail OPEN. `currentUser` never emits if `users/me` errors, which would
    // otherwise hang the router indefinitely; an empty page beats a dead spinner.
    // The real fix is the 401 handling in AppComponent — this is the backstop.
    timeout({ first: 15000, with: () => of(true) }),
  );
};
